import React, { useContext, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ThemeContext } from 'styled-components';
import * as s from './styled-chart-scatter';

const formatNum = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) < 100) return v.toFixed(2);
  if (Math.abs(v) < 1000) return v.toFixed(1);
  return Math.round(v).toString();
};

// Dispersao (ponto de operacao): pareia a variavel PRIMARIA (eixo X) com
// uma 2a variavel (eixo Y) pelo MESMO timestamp. Para EBA, vazao x potencia
// revela o ponto de operacao vs faixa ideal (BEP); generaliza pra qualquer
// par de variaveis. Sem 2a variavel selecionada, instrui o operador.
const ChartScatter = ({ xData, xLabel, xUnit, ySeries }) => {
  const { colors } = useContext(ThemeContext);

  const points = useMemo(() => {
    if (!xData || !xData.length || !ySeries || !ySeries.data || !ySeries.data.length) {
      return [];
    }
    // Indexa Y por timestamp pra lookup O(1) e pareia com X no mesmo ts.
    const yByTs = new Map();
    ySeries.data.forEach((p) => {
      const v = Number(p.value);
      if (Number.isFinite(v)) yByTs.set(p.timestamp, v);
    });
    const out = [];
    xData.forEach((p) => {
      const x = Number(p.value);
      if (!Number.isFinite(x)) return;
      const y = yByTs.get(p.timestamp);
      if (Number.isFinite(y)) out.push({ x, y });
    });
    return out;
  }, [xData, ySeries]);

  if (!ySeries) {
    return (
      <s.Container>
        <s.Header>
          <s.Title>Dispersão (ponto de operação)</s.Title>
        </s.Header>
        <s.EmptyMessage>
          Selecione uma 2ª variável (botão de comparar variáveis no topo) para
          cruzar com {xLabel || 'a variável atual'} — ex.: vazão × potência.
        </s.EmptyMessage>
      </s.Container>
    );
  }

  if (!points.length) {
    return (
      <s.Container>
        <s.Header>
          <s.Title>
            Dispersão (ponto de operação)
            <s.SubTitle>
              — {xLabel} × {ySeries.name}
            </s.SubTitle>
          </s.Title>
        </s.Header>
        <s.EmptyMessage>
          Sem pontos coincidentes no tempo entre as duas variáveis nesta janela.
        </s.EmptyMessage>
      </s.Container>
    );
  }

  return (
    <s.Container>
      <s.Header>
        <s.Title>
          Dispersão (ponto de operação)
          <s.SubTitle>
            — {xLabel} × {ySeries.name}
          </s.SubTitle>
        </s.Title>
        <s.InfoItem>
          Pontos: <strong>{points.length.toLocaleString('pt-BR')}</strong>
        </s.InfoItem>
      </s.Header>
      <s.ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 12, right: 20, bottom: 24, left: 8 }}>
            <CartesianGrid
              stroke={colors.disabled}
              strokeDasharray="3 3"
              opacity={0.4}
            />
            <XAxis
              type="number"
              dataKey="x"
              name={xLabel}
              tick={{ fill: colors.label, fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: colors.disabled, strokeOpacity: 0.5 }}
              tickFormatter={(v) => formatNum(v)}
              label={{
                value: `${xLabel || 'X'}${xUnit ? ` (${xUnit})` : ''}`,
                position: 'insideBottom',
                fill: colors.label,
                fontSize: 10,
                offset: -10,
              }}
            />
            <YAxis
              type="number"
              dataKey="y"
              name={ySeries.name}
              tick={{ fill: colors.label, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={(v) => formatNum(v)}
              label={{
                value: `${ySeries.name}${ySeries.unit ? ` (${ySeries.unit})` : ''}`,
                angle: -90,
                position: 'insideLeft',
                fill: colors.label,
                fontSize: 10,
                style: { textAnchor: 'middle' },
              }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3', stroke: colors.disabled }}
              contentStyle={{
                background: colors.card,
                border: `1px solid ${colors.disabled}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value, name) => [
                formatNum(value),
                name === 'x' ? xLabel : ySeries.name,
              ]}
            />
            <Scatter
              data={points}
              fill={ySeries.color || colors.lightblue}
              fillOpacity={0.45}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </s.ChartWrapper>
    </s.Container>
  );
};

export default React.memo(ChartScatter);
